import { Accordion, AccordionItem, AccordionItemHeading, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion';
import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Modal from 'react-modal';

// Components
import SearchResultsModal from '../organisms/modals/SearchResultsModal';
import Footer from '../organisms/Footer';
import Hero from '../organisms/Hero';

// API
import { searchPeople } from '../../api/people';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    column-gap: 32px;
    row-gap: 24px;

    @media (max-width: 480px) {
        flex-direction: column;
        margin-bottom: 8px;
        max-width: 90%;
        margin-left: auto;
        margin-right: auto;
    }

    @media (max-width: 1200px) and (min-width: 480px) {
        max-width: 95%;
    }
`;

const FullColumn = styled.div`
    width: 100%;
`;

const SpacedFullColumn = styled(FullColumn)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 12px;
    }
`;

const HalfColumn = styled.div`
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    @media (max-width: 480px) {
        width: 100%;
        &:not(:last-child) {
            margin-bottom: 12px;
        }
    }
`;

const ThirdsColumn = styled.div`
    width: 33%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    @media (max-width: 480px) {
        width: 100%;
        &:not(:last-child) {
            margin-bottom: 12px;
        }
    }
`;

const GridImage = styled.img`
    width: 100%;
    height: ${props => props.height || 'auto'};
    border-radius: 12px;
    object-fit: cover;
    object-position: center;
    box-shadow: 0px 4px 5px rgba(0,0,0,0.25);
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0px 6px 10px rgba(0,0,0,0.25);
    }
`;

const ModalImage = styled.img`
    width: 100%;
    height: 100%;
    max-height: 95vh;
    object-fit: contain;

    @media (max-width: 600px) {
        width: 100%;
        height: auto;
    }
`;

const LightBackgroundContainer = styled(Container)`
    background-color: #fafafa; 
    padding-top: 3rem; 
    padding-bottom: 3rem;
`;

const PrimaryBackgroundContainer = styled(Container)`
    background-color: #56050D;
    padding-top: 3rem; 
    padding-bottom: 3rem;
`;

const FAQAccordion = styled(Accordion)`
    border: #56050D 2px solid;
    color: #2a2a2a;

    // Not last child
    & > *:not(:last-child) {
        border-bottom: #ddd 1px solid;
    }
`;

const FAQAccordionItem = styled(AccordionItem)`
`;

const FAQAccordionItemButton = styled(AccordionItemButton)`
    background-color: ${props => props.isCollapsed ? '#fafafa' : '#3c0309'};
    padding: 16px;
    font-size: 18px;
    font-weight: 500;
    color: ${props => props.isCollapsed ? '#2a2a2a' : '#f8f8f8'};
    border-radius: 0px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;

    display: flex;
    justify-content: space-between;
    align-items: center;

    svg {
        transition: transform 0.15s ease-in-out;
        transform: ${props => props.isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
    }

    &:hover {
        background-color: ${props => props.isCollapsed ? '#f8f8f8' : '#2b0206'};
    }
`;

const FAQAccordionItemPanel = styled(AccordionItemPanel)`
    padding: 16px;
    background-color: #f8f8f8;
`;

const FAQAccordionText = styled.p`
    color: #2a2a2a;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.5;
    margin: 0;
    font-family: 'Lexend', serif;
`;

const CalloutTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;

    @media (max-width: 480px) {
        order: 1;
        width: 100%;
    }
`;

const CalloutTextHeader = styled.h2`
    color: #f8f8f8;
    font-family: Merriweather, serif;
    margin: 0px;
    font-size: 28px;

    @media (max-width: 480px) {
        text-align: center;
        width: 100%;
    }
`;

const CalloutTextSubtitle = styled.p`
    color: #f8f8f8;
    font-family: Lexend, serif;
    font-weight: 300;
    font-size: 18px;
    margin-top: 0px; 
    margin-bottom: 0px;

    @media (max-width: 480px) {
        text-align: center;
        width: 100%;
    }
`;

const CalloutButton = styled.a`
    color: #56050D;
    font-size: 18px; 
    font-weight: 500;
    text-decoration: none; 
    display: flex;
    padding: 12px 24px;
    background-color: #fff;
    border-radius: 8px; 
    transition: all 0.15s ease-in-out;
    cursor: pointer;

    @media (max-width: 480px) {
        order: 2;
        margin-top: 12px;
    }
`;

const FAQ_QUESTION_COUNT = 12;

const Homepage = (props) => {
    // API Data
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [peopleSearchResults, setPeopleSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [expandedItems, setExpandedItems] = useState(new Array(FAQ_QUESTION_COUNT).fill(false));

    // User Inputs
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    // UI
    const [imageHeight, setImageHeight] = useState('auto');
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');

    const columnRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const updateHeight = () => {
            if (columnRef.current) {
                const width = columnRef.current.offsetWidth;
                setImageHeight(`${width}px`);
            }
        };
    
        // Update the height when the component mounts
        updateHeight();
    
        // Add a resize event listener to update the height when the window width changes
        window.addEventListener('resize', updateHeight);
    
        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('resize', updateHeight);
        };
    }, []);

    const handleSubmitSearch = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const searchResults = await searchPeople(name, email);
            setPeopleSearchResults(searchResults);
            setIsSearchModalOpen(true);
        } catch (error) {
            console.error("🚀 ~ file: Hero.jsx:183 ~ handleSubmit ~ error", error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleResultClick = (person) => {
        setIsSearchModalOpen(false);
        navigate(`/person/${person.id}`, { person });
    }

    const openImageModal = (imageSrc) => {
        setSelectedImage(imageSrc);
        setIsImageModalOpen(true);
    }

    const closeImageModal = () => {
        setSelectedImage('');
        setIsImageModalOpen(false);
    }

    return (
        <Root>
            <SearchResultsModal
                isOpen={isSearchModalOpen}
                onClickResult={handleResultClick}
                onClose={() => setIsSearchModalOpen(false)}
                searchResults={peopleSearchResults}
            />

            <Hero
                isLoading={isLoading}
                name={name} setName={setName}
                email={email} setEmail={setEmail}
                handleSubmit={handleSubmitSearch}
            />
            
            {/* Story Section */}
            <LightBackgroundContainer>
                <Row>
                    <FullColumn>
                        <h2 style={{ color: '#0a0a0a', textAlign: 'center', fontFamily: 'Merriweather, serif', margin: 0, fontSize: 28 }}>Our Story</h2>
                    </FullColumn>
                </Row>
                <Row style={{ marginTop: '24px' }}>
                    <HalfColumn>
                        <h4 style={{ color: '#0a0a0a', fontFamily: 'Merriweather, serif', fontSize: 20 }}>Sunny</h4>
                        <hr style={{ width: '100%', margin: '0 auto', backgroundColor: '#f8f8f8' }} />
                        <p style={{ color: '#0a0a0a', fontFamily: 'Lexend, serif', fontWeight: 300 }}>In the year 2018, fate brought Simron and me together during a time when we were both seeking guidance and new beginnings. Our initial meeting was filled with courteous conversation and a sense of respect for each other's life experiences.</p>
                        <p style={{ color: '#0a0a0a', fontFamily: 'Lexend, serif', fontWeight: 300 }}>While we may have come from different backgrounds and held varied viewpoints, we quickly discovered a shared commitment to kindness, honesty, and the pursuit of personal growth. This mutual understanding, nurtured over the past five years, has blossomed into a strong and cherished bond.</p>
                        <p style={{ color: '#0a0a0a', fontFamily: 'Lexend, serif', fontWeight: 300 }}>Throughout our journey, Simron's unwavering optimism and compassion have been a constant source of inspiration. Her strength and dedication continue to motivate me to become the best version of myself. It is with immense gratitude and love that I stand here today, ready to start a new chapter in life, hand-in-hand with her.</p>
                        <p style={{ color: '#0a0a0a', fontFamily: 'Lexend, serif', fontWeight: 300 }}>Looking forward, we are filled with hope and excitement for the future that awaits us as husband and wife. We remain committed to nurturing our bond, built on respect, understanding, and unwavering love. Together, we are confident that we can face life's challenges and create a life filled with happiness and fulfillment.</p>
                    </HalfColumn>

                    <HalfColumn>
                        <h4 style={{ color: '#0a0a0a', fontFamily: 'Merriweather, serif', fontSize: 20 }}>Simron</h4>
                        <hr style={{ width: '100%', margin: '0 auto', backgroundColor: '#f8f8f8' }} />
                        <p style={{ color: '#0a0a0a', fontFamily: 'Lexend, serif', fontWeight: 300 }}>In the year 2018, my life took an unexpected turn. I was looking for a change of pace after a difficult time, and fate had something special in store. After a single swipe, I met someone named Sunny. His opening message was a bit unusual, but his enthusiasm was undeniable. We decided to meet in person, and while our first date wasn't exactly perfect - it involved a close call with traffic and a surprising library visit - it marked the beginning of a wonderful friendship.</p>
                        <p style={{ color: '#0a0a0a', fontFamily: 'Lexend, serif', fontWeight: 300 }}>Over the following weeks, I got to know Sunny better. He was kind and supportive, even offering to help me with a challenging school assignment. Our differences started to seem less important, and a deeper connection began to form. On a special December 1st evening, we celebrated with a drink and shared an unforgettable adventure exploring our college campus. It was then that Sunny asked me to be his girlfriend.</p>
                        <p style={{ color: '#0a0a0a', fontFamily: 'Lexend, serif', fontWeight: 300 }}>For three months, we shared our lives as friends and grew closer each day. We learned that even though we were different in some ways, we were a good fit for each other. Our bond grew stronger with each passing day, and five years later, we're ready to embark on a new chapter as husband and wife. Sunny brought joy and excitement into my life, and I can't wait to see what the future holds for us as a couple. Our story is a reminder that sometimes, the most unexpected friendship can blossom into something truly wonderful.</p>
                    </HalfColumn>
                </Row>
            </LightBackgroundContainer>

            {/* Registry Callout Section */}
            <PrimaryBackgroundContainer>
                <Row>
                    <SpacedFullColumn>
                        <CalloutTextContainer>
                            <CalloutTextHeader>Shower Them with Gifts</CalloutTextHeader>
                            <CalloutTextSubtitle>Help them start their happily ever after!</CalloutTextSubtitle>
                        </CalloutTextContainer>
                        <CalloutButton href="https://www.zola.com/registry/simronandsunny" target="_blank" rel="noreferrer">View Registry</CalloutButton>
                    </SpacedFullColumn>
                </Row>
            </PrimaryBackgroundContainer>
            
            {/* Gallery Section */}
            <LightBackgroundContainer>
                <Row>
                    <FullColumn>
                        <h2 style={{ color: '#2a2a2a', textAlign: 'center', fontFamily: 'Merriweather, serif', margin: 0, fontSize: 28 }}>Meet the Bride & Groom</h2>
                    </FullColumn>
                </Row>

                <Row style={{ marginTop: 64 }}>
                    <ThirdsColumn ref={columnRef}>
                        <GridImage
                            src={`https://i.ibb.co/7gSbkgC/IMG-5419.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/7gSbkgC/IMG-5419.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/JtZDkG0/IMG-5417.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/JtZDkG0/IMG-5417.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/MV778V4/IMG-5418.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/MV778V4/IMG-5418.jpg`)}
                        />
                    </ThirdsColumn>
                </Row>

                <Row style={{ marginTop: 32 }}>
                    <ThirdsColumn ref={columnRef}>
                        <GridImage
                            src={`https://i.ibb.co/NYbLCMc/IMG-5528.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/NYbLCMc/IMG-5528.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/zhqKbYf/IMG-5535.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/zhqKbYf/IMG-5535.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/WstwXkn/IMG-5532.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/WstwXkn/IMG-5532.jpg`)}
                        />
                    </ThirdsColumn>
                </Row>

                <Row style={{ marginTop: 32 }}>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://media-api.xogrp.com/images/1af6cd51-bec2-432e-88ac-7b10c0056087~rt_auto-sc_300.300?ordering=explicit`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://media-api.xogrp.com/images/1af6cd51-bec2-432e-88ac-7b10c0056087~rt_auto-sc_300.300?ordering=explicit`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://media-api.xogrp.com/images/cc0eb49d-3646-428a-ad59-c3c508262ca2~rt_auto-sc_300.300?ordering=explicit`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://media-api.xogrp.com/images/cc0eb49d-3646-428a-ad59-c3c508262ca2~rt_auto-sc_300.300?ordering=explicit`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://media-api.xogrp.com/images/869fd069-a57f-4e0b-a0c8-dd3fd7784c07~rt_auto-sc_300.300?ordering=explicit`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://media-api.xogrp.com/images/869fd069-a57f-4e0b-a0c8-dd3fd7784c07~rt_auto-sc_300.300?ordering=explicit`)}
                        />
                    </ThirdsColumn>
                </Row>

                <Row style={{ marginTop: 32 }}>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/frGdksY/IMG-5526.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/frGdksY/IMG-5526.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://media-api.xogrp.com/images/8dc5334f-8054-4504-bdcb-4fd0172ab4ac~rt_auto-sc_300.300?ordering=explicit`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://media-api.xogrp.com/images/8dc5334f-8054-4504-bdcb-4fd0172ab4ac~rt_auto-sc_300.300?ordering=explicit`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/gS2d4SK/IMG-5527.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/gS2d4SK/IMG-5527.jpg`)}
                        />
                    </ThirdsColumn>
                </Row>

                <Row style={{ marginTop: 32 }}>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/VwdVdgk/IMG-5529.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/VwdVdgk/IMG-5529.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/0ZtwVpk/IMG-5530.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/0ZtwVpk/IMG-5530.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://media-api.xogrp.com/images/7987f957-157c-4d2b-b3c1-d6fe4ae04f2e~rt_auto-sc_300.300?ordering=explicit`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://media-api.xogrp.com/images/7987f957-157c-4d2b-b3c1-d6fe4ae04f2e~rt_auto-sc_300.300?ordering=explicit`)}
                        />
                    </ThirdsColumn>
                </Row>

                <Row style={{ marginTop: 32 }}>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/Hqr38NB/IMG-5534.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/Hqr38NB/IMG-5534.jpg`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://media-api.xogrp.com/images/f89ae345-ae13-48ad-869b-736148ba290a~rt_auto-sc_300.300?ordering=explicit`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://media-api.xogrp.com/images/f89ae345-ae13-48ad-869b-736148ba290a~rt_auto-sc_300.300?ordering=explicit`)}
                        />
                    </ThirdsColumn>
                    <ThirdsColumn>
                        <GridImage
                            src={`https://i.ibb.co/QJmqbF0/IMG-5608.jpg`}
                            height={imageHeight}
                            onClick={() => openImageModal(`https://i.ibb.co/QJmqbF0/IMG-5608.jpg`)}
                        />
                    </ThirdsColumn>
                </Row>
            </LightBackgroundContainer>

            {/* FAQ Section */}
            <PrimaryBackgroundContainer>
                <Row>
                    <FullColumn>
                        <h2 style={{ color: '#fafafa', textAlign: 'center', fontFamily: 'Merriweather, serif', margin: 0, fontSize: 28 }}>FAQs</h2>
                    </FullColumn>
                </Row>
                <Row>
                    <FullColumn style={{ marginTop: 32 }}>
                        <FAQAccordion allowZeroExpanded allowMultipleExpanded>
                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 0 ? !item : item))}>
                                    <FAQAccordionItemButton isCollapsed={!expandedItems[0]}>
                                        WHEN IS THE WEDDING?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Please RSVP and you will see the information for the wedding events and get a confirmation email. If more questions please contact Bikash Dhali at <a href="tel:8473408257">847-340-8257</a> or Rina Bala at <a href="tel:8373479240">847-347-9240</a>.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 1 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[1]}
                                    >
                                        WHAT CAN I WEAR?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Please check your RSVP and email confirmation for dress attire for all the events.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 2 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[2]}
                                    >
                                        WHAT COLORS CAN I WEAR?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Please check your RSVP and email confirmation for dress attire for all the events.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 3 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[3]}
                                    >
                                        ARE CHILDREN OR PLUS-ONES ALLOWED?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>While we adore your children, we regret that  our venue has limited capacity. Please refer to your RSVP to confirm the names included in your invitation.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 4 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[4]}
                                    >
                                        WHAT IS THE DEADLINE FOR THE RSVP?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Please RSVP by April 30th, 2024 using the form above on this website.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 5 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[5]}
                                    >
                                        HOW DO I GET TO THE VENUE?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Location and time for each event is mentioned in your RSVP and email confirmation. A discounted hotel room block has been reserved at the Chicago Marriott Oak Brook. To book your room, call the hotel and mention <strong>"Simron & Ishvdeep Wedding"</strong> or please visit this <a target="_blank" rel="noreferrer" href="https://www.marriott.com/event-reservations/reservation-link.mi?id=1704322369143&key=GRP">special link.</a> </FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 6 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[6]}
                                    >
                                        HOW IS PARKING?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Ample parking with over 100 spaces is available at the venue.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 7 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[7]}
                                    >
                                        CAN WE SMOKE OR DRINK?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Alcoholic beverages will be served at designated times during the events. We ask that you drink responsibly.  To respect the venue and other guests, smoking is only permitted outside in designated areas.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 8 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[8]}
                                    >
                                        WILL FOOD BE PROVIDED?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>Yes! Delicious food will be served at each event. Should you have any dietary restrictions, please let us know when you RSVP.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 9 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[9]}
                                    >
                                        WHAT IS THE SCHEDULE OF EVENTS?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>A detailed schedule of events is included in your confirmation email.</FAQAccordionText>
                                    <FAQAccordionText>We can't wait to celebrate with you!</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 10 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[10]}
                                    >
                                        WHAT ABOUT GIFTS?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>We're so grateful for your love and support! If you'd like to give a gift, we've created a registry to help us start our new life together. You can find it <a href="https://www.zola.com/registry/simronandsunny" target="_blank" rel="noreferrer">here</a>. <strong>No boxed gifts please.</strong></FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>

                            <FAQAccordionItem>
                                <AccordionItemHeading onClick={() => setExpandedItems(prev => prev.map((item, index) => index === 11 ? !item : item))}>
                                    <FAQAccordionItemButton
                                        isCollapsed={!expandedItems[11]}
                                    >
                                        WHERE CAN WE STAY?
                                        <FaChevronDown />
                                    </FAQAccordionItemButton>
                                </AccordionItemHeading>
                                <FAQAccordionItemPanel>
                                    <FAQAccordionText>For your convenience, we've reserved a block of rooms at the hotel connected to the venue. You can easily book your stay <a href="https://www.marriott.com/event-reservations/reservation-link.mi?id=1704322369143&key=GRP" rel="noreferrer" target="_blank">here</a> or <a href="tel:">call</a> the hotel and mention <strong>"Simron and Ishvdeep Wedding"</strong>.</FAQAccordionText>
                                </FAQAccordionItemPanel>
                            </FAQAccordionItem>
                            {/* Add more AccordionItems as needed */}
                        </FAQAccordion>
                    </FullColumn>
                </Row>
            </PrimaryBackgroundContainer>

            <Footer />

            <Modal
                isOpen={isImageModalOpen}
                onRequestClose={closeImageModal}
                contentLabel="Image Modal"
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)' },
                    content: {
                        color: 'lightsteelblue',
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                        border: 'none',
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%', // Set a default width
                        maxWidth: '600px', // Limit the maximum width
                        padding: '20px', // Add some padding
                        '@media (max-width: 600px)': { // Adjust the styles for screens smaller than 600px
                            width: '100%',
                            height: 'auto',
                            top: '0',
                            left: '0',
                            transform: 'none',
                            marginRight: '0',
                            padding: '10px',
                        }
                    }
                }}
            >
                <button onClick={closeImageModal} style={{ position: 'absolute', top: 32, right: 32, fontSize: 24, color: '#56050D', backgroundColor: 'transparent', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>✕</button>
                <ModalImage src={selectedImage} alt="" />
            </Modal>
        </Root>
    );
};

export default Homepage;